/**
 * <CloudIntegrationsCarousel /> — horizontal card carousel of CARTIE's
 * cloud + data integrations, styled after the reference shared by the
 * user (large rounded color-glow icon · bold title · description · pill
 * tag · left/right chevrons · dot indicators).
 *
 * v34.10 (Feb 2026): real provider brand logos via Simple Icons CDN
 * (MIT-licensed, retina-crisp SVG) replacing the abstract Lucide icons.
 * Each card now glows with its true brand color (AWS orange · Azure
 * blue · GCP multi · Snowflake cyan · Databricks red · DO blue · K8s
 * blue). The two SOON multi-vendor cards keep Lucide fallbacks.
 *
 * v34.2 (Mar 2026): replaces the previous "ledger row" layout on the
 * landing page. Same data, way more visually interesting.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Cloud, ChevronLeft, ChevronRight, ArrowRight,
  Network, Boxes, ShieldCheck,
} from 'lucide-react';

// Brand logos via Iconify CDN (MIT-licensed, retina-crisp SVG).
//   - `logos:*` set serves multi-color brand-accurate SVG
//   - `simple-icons:*` set serves monochrome — we pass ?color=<hex> to tint
// All slugs verified against api.iconify.design Feb 2026.
const ICONIFY = (id, hex) => {
  const url = `https://api.iconify.design/${id}.svg`;
  return hex ? `${url}?color=%23${hex}` : url;
};

const PROVIDERS = [
  {
    id: 'aws',
    name: 'AWS',
    tag: 'LIVE',
    logo: ICONIFY('logos:aws'),
    brand: 'orange',
    desc: 'Cost Explorer + CUR. EC2 / RDS / S3 / Lambda / NAT / EBS rightsizing. Spot intelligence. RI & Savings Plan optimizer.',
    pill: 'Cost Explorer · CUR · RI/SP',
    to: '/cloud-integrations',
  },
  {
    id: 'azure',
    name: 'Azure',
    tag: 'LIVE',
    logo: ICONIFY('logos:microsoft-azure'),
    brand: 'azure',
    desc: 'Cost Management API + CSV ingestion. Hybrid Benefit, RI optimizer, regional drift, subscription rollup.',
    pill: 'Cost Mgmt API · Hybrid Benefit',
    to: '/cloud-integrations',
  },
  {
    id: 'gcp',
    name: 'Google Cloud',
    tag: 'LIVE',
    logo: ICONIFY('logos:google-cloud'),
    brand: 'google',
    desc: 'BigQuery billing export. CUDs / sustained-use optimization. Per-project / per-folder roll-up & forecasting.',
    pill: 'Billing Export · CUD optimizer',
    to: '/cloud-integrations',
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    tag: 'LIVE',
    logo: ICONIFY('logos:snowflake-icon'),
    brand: 'snowflake',
    desc: 'Account Usage queries. Warehouse rightsizing, auto-suspend tuning, query-cost attribution, materialized-view ROI.',
    pill: 'Warehouse · Auto-suspend · MV ROI',
    to: '/snowflake-health-score',
  },
  {
    id: 'databricks',
    name: 'Databricks',
    tag: 'LIVE',
    logo: ICONIFY('simple-icons:databricks', 'FF3621'),
    brand: 'databricks',
    desc: 'Cluster rightsize, Photon ROI, spot policy, autoscaling tuning, job-level cost attribution.',
    pill: 'Cluster rightsize · Photon ROI',
    to: '/cloud-integrations',
  },
  {
    id: 'do',
    name: 'DigitalOcean',
    tag: 'LIVE',
    logo: ICONIFY('simple-icons:digitalocean', '0080FF'),
    brand: 'do',
    desc: 'Droplet, App Platform, K8s & Spaces cost analysis. Idle droplet hunter + reserved IP cleanup.',
    pill: 'Droplet · K8s · Idle hunter',
    to: '/cloud-integrations',
  },
  {
    id: 'k8s',
    name: 'Kubernetes',
    tag: 'LIVE',
    logo: ICONIFY('logos:kubernetes'),
    brand: 'k8s',
    desc: 'Monte-Carlo Shapley attribution across namespaces. Idle pod hunter, request/limit drift, cost-fair chargebacks.',
    pill: 'Shapley · Idle pods · Chargebacks',
    to: '/k8s-cost',
  },
  // v34.21 (Feb 2026): split the previous 2 stacked-logo SOON cards into
  // 6 individual cards — one per provider. The stacked-logo treatment
  // looked cluttered and unreadable on white tiles per user feedback.
  // Each provider now gets its own clean tile with its real logo.
  {
    id: 'oracle',
    name: 'Oracle Cloud',
    tag: 'SOON',
    logo: ICONIFY('logos:oracle'),
    brand: 'oracle',
    desc: 'OCI cost analytics, Exadata pricing optimization, autonomous DB rightsizing. Production roll-out post-May 2026 launch.',
    pill: 'OCI · Exadata · Autonomous',
    to: '/cloud-integrations',
  },
  {
    id: 'ibm',
    name: 'IBM Cloud',
    tag: 'SOON',
    logo: ICONIFY('logos:ibm'),
    brand: 'ibm',
    desc: 'IBM Cloud Pak, Watson cost tracking, hybrid mainframe + cloud cost reconciliation. Integration queued.',
    pill: 'Cloud Pak · Watson · Mainframe',
    to: '/cloud-integrations',
  },
  {
    id: 'alibaba',
    name: 'Alibaba Cloud',
    tag: 'SOON',
    logo: ICONIFY('simple-icons:alibabacloud', 'FF6A00'),
    brand: 'alibaba',
    desc: 'Aliyun ECS, MaxCompute, OSS cost attribution. APAC-region optimization. Integration queued behind May launch.',
    pill: 'ECS · MaxCompute · OSS',
    to: '/cloud-integrations',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    tag: 'SOON',
    logo: ICONIFY('logos:cloudflare'),
    brand: 'cloudflare',
    desc: 'Workers, R2 storage, CDN egress cost tracking. Per-zone attribution. Replace S3 with R2 ROI calculator.',
    pill: 'Workers · R2 · CDN egress',
    to: '/cloud-integrations',
  },
  {
    id: 'akamai',
    name: 'Linode · Akamai',
    tag: 'SOON',
    logo: ICONIFY('logos:akamai'),
    brand: 'akamai',
    desc: 'Akamai Cloud Computing (formerly Linode), Object Storage, edge compute cost analysis.',
    pill: 'Compute · Storage · Edge',
    to: '/cloud-integrations',
  },
  {
    id: 'hetzner',
    name: 'Hetzner',
    tag: 'SOON',
    logo: ICONIFY('simple-icons:hetzner', 'D50C2D'),
    brand: 'hetzner',
    desc: 'EU-region cloud servers, object storage, dedicated hardware cost tracking. Per-€ optimization.',
    pill: 'Cloud · Storage · Dedicated',
    to: '/cloud-integrations',
  },
];

// Per-brand glow + hover-border tokens (Tailwind JIT must see all literals).
const BRAND = {
  orange:     { glow: 'bg-orange-500/30',  ring: 'ring-orange-500/30',  border: 'hover:border-orange-400/60',  pill: 'bg-orange-500/10 text-orange-300 border-orange-500/30' },
  azure:      { glow: 'bg-sky-500/30',     ring: 'ring-sky-500/30',     border: 'hover:border-sky-400/60',     pill: 'bg-sky-500/10 text-sky-300 border-sky-500/30' },
  google:     { glow: 'bg-blue-500/30',    ring: 'ring-blue-500/30',    border: 'hover:border-blue-400/60',    pill: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  snowflake:  { glow: 'bg-cyan-500/30',    ring: 'ring-cyan-500/30',    border: 'hover:border-cyan-400/60',    pill: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30' },
  databricks: { glow: 'bg-red-500/30',     ring: 'ring-red-500/30',     border: 'hover:border-red-400/60',     pill: 'bg-red-500/10 text-red-300 border-red-500/30' },
  do:         { glow: 'bg-blue-600/30',    ring: 'ring-blue-600/30',    border: 'hover:border-blue-500/60',    pill: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  k8s:        { glow: 'bg-indigo-500/30',  ring: 'ring-indigo-500/30',  border: 'hover:border-indigo-400/60',  pill: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30' },
  amber:      { glow: 'bg-amber-500/30',   ring: 'ring-amber-500/30',   border: 'hover:border-amber-400/60',   pill: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
  // v34.21 — per-provider brand tints for the 6 individual SOON cards
  oracle:     { glow: 'bg-red-600/30',     ring: 'ring-red-600/30',     border: 'hover:border-red-500/60',     pill: 'bg-red-500/10 text-red-300 border-red-500/30' },
  ibm:        { glow: 'bg-blue-700/30',    ring: 'ring-blue-700/30',    border: 'hover:border-blue-600/60',    pill: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  alibaba:    { glow: 'bg-orange-500/30',  ring: 'ring-orange-500/30',  border: 'hover:border-orange-400/60',  pill: 'bg-orange-500/10 text-orange-300 border-orange-500/30' },
  cloudflare: { glow: 'bg-orange-500/30',  ring: 'ring-orange-500/30',  border: 'hover:border-orange-400/60',  pill: 'bg-orange-500/10 text-orange-300 border-orange-500/30' },
  akamai:     { glow: 'bg-blue-500/30',    ring: 'ring-blue-500/30',    border: 'hover:border-blue-400/60',    pill: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  hetzner:    { glow: 'bg-red-500/30',     ring: 'ring-red-500/30',     border: 'hover:border-red-400/60',     pill: 'bg-red-500/10 text-red-300 border-red-500/30' },
};

const CARD_WIDTH = 320; // px
const GAP = 20;

const CloudIntegrationsCarousel = () => {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / (CARD_WIDTH + GAP));
    setActiveIdx(Math.min(idx, PROVIDERS.length - 1));
  }, []);

  const scrollByCards = (n) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: n * (CARD_WIDTH + GAP), behavior: 'smooth' });
  };

  const jumpTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * (CARD_WIDTH + GAP), behavior: 'smooth' });
  };

  // Auto-scroll (loops back to start on hitting the end)
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 20;
      if (atEnd) el.scrollTo({ left: 0, behavior: 'smooth' });
      else el.scrollBy({ left: CARD_WIDTH + GAP, behavior: 'smooth' });
    }, 5000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section
      id="cloud-integrations"
      className="py-14 px-4 relative overflow-hidden scroll-mt-24"
      data-testid="landing-cloud-integrations"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
            <Cloud className="w-3 h-3" /> 6 live · 9 integrating · read-only · 2-min setup
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1] text-white max-w-4xl mx-auto">
            One dashboard for{' '}
            <span className="text-emerald-400 whitespace-nowrap">every cloud you use</span>.
          </h2>
          <p className="mt-3 text-base text-slate-300">
            Native, deep integrations — not surface-level scrapes. The right API per provider · credentials never stored.
          </p>
        </div>

        {/* Carousel container */}
        <div className="relative">
          {/* Arrow buttons */}
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            aria-label="Previous integration"
            className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-slate-900 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-400/70 hover:text-white items-center justify-center shadow-xl shadow-black/30 transition-all hover:-translate-y-1/2 hover:scale-110"
            data-testid="ci-carousel-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            aria-label="Next integration"
            className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-slate-900 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-400/70 hover:text-white items-center justify-center shadow-xl shadow-black/30 transition-all hover:-translate-y-1/2 hover:scale-110"
            data-testid="ci-carousel-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Edge fades */}
          <div className="hidden md:block pointer-events-none absolute left-0 top-0 bottom-4 w-12 z-10 bg-slate-950 bg-gradient-to-r from-slate-950 to-transparent" />
          <div className="hidden md:block pointer-events-none absolute right-0 top-0 bottom-4 w-12 z-10 bg-slate-950 bg-gradient-to-l from-slate-950 to-transparent" />

          {/* Scroller */}
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            className="overflow-x-auto scrollbar-none snap-x snap-mandatory py-2 px-2 md:px-12"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-5">
              {PROVIDERS.map((p) => {
                const b = BRAND[p.brand] || BRAND.amber;
                const isLive = p.tag === 'LIVE';
                const Fallback = p.icon || Cloud;
                return (
                  <Link
                    key={p.id}
                    to={p.to}
                    data-testid={`ci-card-${p.id}`}
                    className={`group shrink-0 w-[320px] snap-start rounded-2xl border border-white/10 bg-slate-900 backdrop-blur p-6 transition-all hover:-translate-y-1 hover:shadow-2xl flex flex-col items-center text-center ${b.border}`}
                  >
                    {/* Icon — real brand logo on a WHITE surface so the
                        brand colors pop crisp. v34.22 (Feb 2026): logo tile
                        centered horizontally per user feedback. Title row
                        below also centered so the card reads top-to-bottom
                        on its vertical axis. */}
                    <div className="relative inline-flex mb-5 mx-auto">
                      <div className={`w-16 h-16 rounded-2xl bg-white ring-1 ${b.ring} flex items-center justify-center relative z-10 overflow-hidden shadow-lg shadow-black/30`}>
                        {p.logoStack ? (
                          <div className="flex -space-x-2">
                            {p.logoStack.map((src, i) => (
                              <img
                                key={i}
                                src={src}
                                alt=""
                                width={26}
                                height={26}
                                loading="lazy"
                                className="w-[26px] h-[26px] rounded-md bg-white p-0.5 ring-1 ring-slate-200/60"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ))}
                          </div>
                        ) : p.logo ? (
                          <img
                            src={p.logo}
                            alt={`${p.name} logo`}
                            width={36}
                            height={36}
                            loading="lazy"
                            className="w-9 h-9 object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <Fallback className="w-7 h-7 text-slate-700" />
                        )}
                      </div>
                      {/* Soft glow halo tinted with the provider's own brand color */}
                      <div className={`absolute inset-0 ${b.glow} rounded-2xl blur-2xl opacity-70 -z-0`} />
                    </div>

                    {/* Title + status pill — v34.22 (Feb 2026): centered
                        title with pill above it on its own row, so the card
                        reads on a single vertical axis. */}
                    <span
                      className={`text-[9px] font-bold tracking-widest rounded-full px-2 py-0.5 border mb-2 ${
                        isLive
                          ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                          : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      }`}
                    >
                      {isLive ? '● LIVE' : 'SOON'}
                    </span>
                    <h3 className="text-xl font-extrabold text-white tracking-tight mb-3">{p.name}</h3>

                    {/* Description */}
                    <p className="text-sm text-slate-400 leading-relaxed mb-4 min-h-[80px]">{p.desc}</p>

                    {/* Capability pill — bottom of card, tinted with brand */}
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono border ${b.pill}`}>
                      {p.pill}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex items-center justify-center gap-1.5 mt-7" data-testid="ci-carousel-dots">
            {PROVIDERS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => jumpTo(i)}
                aria-label={`Go to integration ${i + 1}`}
                className={`transition-all rounded-full ${i === activeIdx ? 'w-7 h-1.5 bg-emerald-400' : 'w-1.5 h-1.5 bg-slate-700 hover:bg-slate-500'}`}
                data-testid={`ci-carousel-dot-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link to="/integrations" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-all" data-testid="ci-open-hub-btn">
            Open Integrations Hub <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/security" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-200 hover:text-white font-medium text-sm transition-all" data-testid="ci-security-btn">
            <ShieldCheck className="w-3.5 h-3.5" /> Stateless audit · read-only IAM
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CloudIntegrationsCarousel;
